import React from 'react';
import { Rocket, Cloud, Server, Database, Wifi, Zap, Globe, Shield, BookOpen, Code, FileText, Map } from 'lucide-react';
import { IsometricCloud, IsometricProgress, IsometricBooks, IsometricCodeBlock, IsometricCertificate } from './PremiumGraphics';

const HeroSection = () => {
  return (
    <section className="min-h-[90vh] flex items-center relative overflow-hidden bg-white pt-24">
      {/* Grid Lines Background */}
      <div className="absolute inset-0 opacity-20">
        <svg width="100%" height="100%" className="absolute inset-0">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#3b82f6" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>
      
      <div className="container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gray-900 block">Go From</span>
              <span className="bg-gradient-to-r from-blue-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
                Zero To Cloud
              </span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Want to learn in-demand skills & get hired?
            </p>
            
            <a href="/courses" className="bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 text-white px-6 py-3 text-lg font-medium shadow-lg rounded-full transition-all duration-200 hover:scale-105 flex items-center w-fit">
              <Rocket className="mr-2" size={20} />
              Explore Courses
            </a>
          </div>

          {/* Right Content - Instructor Image */}
          <div className="relative flex justify-center">
            <div className="relative">
              <img 
                src="/instructor-thumbnail.png" 
                alt="Lucy - Your Instructor with 5,000+ Students, 200K Subscribers, 4.9 Star Rating"
                className="w-84 h-auto rounded-xl shadow-2xl border-4 border-white"
              />
              {/* Subtle background decoration */}
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-100 to-teal-100 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
        
        {/* Integrated Features Section */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="text-center p-6 rounded-xl bg-gray-50 border border-blue-100 hover:bg-blue-50 transition-all duration-300">
            <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
              <BookOpen className="text-blue-600" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Study Notes
            </h3>
            <p className="text-sm text-gray-600">
              Comprehensive study materials and cheat sheets
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-gray-50 border border-blue-100 hover:bg-blue-50 transition-all duration-300">
            <div className="w-16 h-16 mx-auto mb-4 bg-teal-100 rounded-lg flex items-center justify-center">
              <Code className="text-teal-600" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Hands-on Projects
            </h3>
            <p className="text-sm text-gray-600">
              Step-by-step instructions to build real-world cloud projects
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-gray-50 border border-blue-100 hover:bg-blue-50 transition-all duration-300">
            <div className="w-16 h-16 mx-auto mb-4 bg-purple-100 rounded-lg flex items-center justify-center">
              <FileText className="text-purple-600" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Practice Exams
            </h3>
            <p className="text-sm text-gray-600">
              Full certification exams with detailed explanations
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-gray-50 border border-blue-100 hover:bg-blue-50 transition-all duration-300">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-lg flex items-center justify-center">
              <Map className="text-green-600" size={24} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Learning Paths
            </h3>
            <p className="text-sm text-gray-600">
              Follow clear roadmaps to get hired in your dream job
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;